import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import { IucnSampleFilter } from 'src/shared/types';
import { fishMetricsQuery, FishMetricsQueryResult } from '../api/queries/fishMetricsQuery';
import { speciesMetricsQuery, SpeciesMetricsQueryResult } from '../api/queries/speciesMetricsQuery';
import { evolutionaryMetricsQuery, EvolutionaryMetricsQueryResult } from '../api/queries/evolutionaryMetricsQuery';
import { speciesRichnessMetricsQuery, SpeciesRichnessMetricsQueryResult } from '../api/queries/speciesRichnessMetricsQuery';
import {
    bacterialFunctionalDiversityMetricsQuery,
    BacterialFunctionalDiversityMetricsQueryResult,
} from '../api/queries/bacterialFunctionalDiversityMetricsQuery';

import {
    fungalFunctionalDiversityMetricsQuery,
    FungalFunctionalDiversityMetricsQueryResult,
} from '../api/queries/fungalFunctionalDiversityMetricsQuery';

import {
    fungalToBacterialRatioMetricsQuery,
    FungalToBacterialRatioMetricsQueryResult,
} from '../api/queries/fungalToBacterialRatioMetricsQuery';

import {
    marineSedimentPollutionIndexMetricsQuery,
    MarineSedimentPollutionIndexMetricsQueryResult,
} from '../api/queries/marineSedimentPollutionIndexMetricsQuery';

import { speciesGroupsMetricsQuery, SpeciesGroupsMetricsQueryResult } from '../api/queries/speciesGroupsMetricsQuery';

type GenericMetricQueryParams = {
    projectId: string;
    habitatAssay: string;
    fromDate?: string;
    toDate?: string;
};

type GenericSamplesQueryParams = {
    projectId: string;
    samples: IucnSampleFilter;
};

const metricsGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        fishMetrics: builder.query<FishMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: fishMetricsQuery,
                variables: params,
            }),
        }),
        speciesMetrics: builder.query<SpeciesMetricsQueryResult, GenericSamplesQueryParams>({
            query: params => ({
                body: speciesMetricsQuery,
                variables: params,
            }),
        }),
        evolutionaryMetrics: builder.query<EvolutionaryMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: evolutionaryMetricsQuery,
                variables: params,
            }),
        }),
        speciesRichnessMetrics: builder.query<SpeciesRichnessMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: speciesRichnessMetricsQuery,
                variables: params,
            }),
        }),
        bacterialFunctionalDiversityMetrics: builder.query<BacterialFunctionalDiversityMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: bacterialFunctionalDiversityMetricsQuery,
                variables: params,
            }),
        }),
        fungalFunctionalDiversityMetrics: builder.query<FungalFunctionalDiversityMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: fungalFunctionalDiversityMetricsQuery,
                variables: params,
            }),
        }),
        fungalToBacterialRatioMetrics: builder.query<FungalToBacterialRatioMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: fungalToBacterialRatioMetricsQuery,
                variables: params,
            }),
        }),
        marineSedimentPollutionIndexMetrics: builder.query<MarineSedimentPollutionIndexMetricsQueryResult, GenericMetricQueryParams>({
            query: params => ({
                body: marineSedimentPollutionIndexMetricsQuery,
                variables: params,
            }),
        }),
        speciesGroupsMetrics: builder.query<SpeciesGroupsMetricsQueryResult['speciesgroups']['data'], GenericMetricQueryParams>({
            query: params => ({
                body: speciesGroupsMetricsQuery,
                variables: params,
            }),
            transformResponse: (response: SpeciesGroupsMetricsQueryResult) => {
                return response?.speciesgroups?.data || [];
            },
        }),
    }),
});

export const {
    useFishMetricsQuery,
    useSpeciesMetricsQuery,
    useSpeciesRichnessMetricsQuery,
    useEvolutionaryMetricsQuery,
    useBacterialFunctionalDiversityMetricsQuery,
    useFungalFunctionalDiversityMetricsQuery,
    useFungalToBacterialRatioMetricsQuery,
    useMarineSedimentPollutionIndexMetricsQuery,
    useSpeciesGroupsMetricsQuery,
} = metricsGraphSlice;

export default metricsGraphSlice.reducer;
